import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid'; // For unique file names

const COMPONENT_IMAGES_BUCKET = 'component-images';

export const imageUploadService = {
  /**
   * Uploads multiple image files to Supabase storage.
   * @param {File[]} files - An array of File objects to upload.
   * @param {string} folderPath - The folder path within the bucket (e.g., 'components/component_id').
   * @param {string} bucketName - The bucket name to upload to. Defaults to COMPONENT_IMAGES_BUCKET.
   * @returns {Promise<string[]>} - A promise that resolves to an array of file paths.
   */
  async uploadImages(files, folderPath, bucketName = COMPONENT_IMAGES_BUCKET) {
    if (!files || files.length === 0) {
      return [];
    }

    const uploadPromises = files.map(async (file) => {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExtension}`;
      const filePath = `${folderPath}/${fileName}`;

      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Error uploading image:', error);
        throw new Error(`Failed to upload image ${file.name}: ${error.message}`);
      }

      return filePath;
    });

    return await Promise.all(uploadPromises);
  },

  /**
   * Deletes an image from Supabase storage using its public URL.
   * @param {string} publicUrl - The public URL of the image to delete.
   * @returns {Promise<void>}
   */
  async deleteImage(publicUrl) {
    if (!publicUrl) return;

    // Extract file path from public URL
    // Example: https://<project_ref>.supabase.co/storage/v1/object/public/component-images/components/some_id/some_file.jpg
    const pathSegments = publicUrl.split('/');
    const bucketIndex = pathSegments.indexOf(COMPONENT_IMAGES_BUCKET);
    
    if (bucketIndex === -1 || bucketIndex + 1 >= pathSegments.length) {
      console.warn('Could not extract file path from public URL for deletion:', publicUrl);
      return;
    }

    const filePath = pathSegments.slice(bucketIndex + 1).join('/');

    const { error } = await supabase.storage
      .from(COMPONENT_IMAGES_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting image:', error);
      throw new Error(`Failed to delete image ${filePath}: ${error.message}`);
    }
  },

  /**
   * Generates a folder path for component images.
   * @param {string} componentId - The ID of the component.
   * @returns {string} The folder path.
   */
  getComponentImagePath(componentId) {
    return `components/${componentId}`;
  },

  /**
   * Generates a signed URL for a given file path within the bucket, allowing temporary authenticated access.
   * @param {string} filePath - The file path within the bucket (e.g., 'components/component_id/image_id.png').
   * @param {number} expiresIn - The number of seconds the signed URL should be valid for (default: 3600 seconds = 1 hour).
   * @returns {Promise<string|null>} - A promise that resolves to the signed URL, or null if an error occurs.
   */
  async getSignedUrl(filePath, expiresIn = 3600) { 
    if (!filePath) return null;

    try {
      const { data, error } = await supabase.storage
        .from(COMPONENT_IMAGES_BUCKET)
        .createSignedUrl(filePath, expiresIn);

      if (error) {
        console.error('Error generating signed URL:', error);
        return null;
      }
      return data?.signedUrl;
    } catch (err) {
      console.error('Exception generating signed URL:', err);
      return null;
    }
  },

  /**
   * Generates a standard public URL for a given file path.
   * This works even for private buckets if an RLS policy allows SELECT for the current user (including anon).
   * @param {string} filePath - The file path within the bucket.
   * @returns {string|null} - The public URL, or null if filePath is missing.
   */
  getPublicUrl(filePath, bucketName = COMPONENT_IMAGES_BUCKET) {
    if (!filePath) return null;

    if (filePath.startsWith('http')) return filePath;

    const { data } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);

    return data?.publicUrl;
  }};